import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  CanLoad,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { AuthService } from '../auth.service';
import { OAuthService } from '../oauth.service';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.model';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate, CanActivateChild, CanLoad {
  /**
   * Constructor
   */
  constructor(
    private userService: UserService,
    private oauth: OAuthService,
    private _router: Router,
    private _authService: AuthService,
    private _location: Location,
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Can activate
   *
   * @param route
   * @param state
   */
  /** SessionStorage key for intended URL when redirect is lost (e.g. server redirects to root before SPA loads). */
  private static readonly STORED_REDIRECT_URL_KEY = 'auth_redirect_url';

  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Promise<boolean | UrlTree> {
    // Apply token and Accept-Language from URL when returning from login app (e.g. ...?access_token=...&Accept-Language=ar)
    const urlHadAuthParams = this._authService.applyLoginRedirectParamsFromUrl();
    // state.url includes path + query string (e.g. /analytics/general?tab=summary); preserve so login redirect returns to same URL
    let redirectURL = state.url === '/sign-out' ? '/' : this._sanitizeUrlForRouting(state.url);
    // If we landed at root (e.g. server redirected /analytics/general -> /), recover intended URL from storage or current URL query
    if ((!redirectURL || redirectURL === '/') && typeof sessionStorage !== 'undefined') {
      const fromStorage = this._sanitizeUrlForRouting(
        sessionStorage.getItem(AuthGuard.STORED_REDIRECT_URL_KEY) || '',
      );
      const fromQuery =
        this._sanitizeUrlForRouting(route.queryParamMap.get('redirectURL') || '') ||
        this._sanitizeUrlForRouting(route.queryParamMap.get('return_url') || '') ||
        (typeof window !== 'undefined' &&
          this._sanitizeUrlForRouting(
            new URLSearchParams(window.location.search).get('redirectURL') || '',
          ));
      redirectURL = fromQuery || fromStorage || redirectURL;
    }

    if (redirectURL && redirectURL !== '/' && !redirectURL.startsWith('/sign-in')) {
      try {
        redirectURL = this._authService.clearTokenParamsFromUrl(redirectURL);
        sessionStorage.setItem(AuthGuard.STORED_REDIRECT_URL_KEY, redirectURL);
      } catch {
        // ignore
      }
    }

    if (this.oauth.isSignedIn()) {
      // Clear access_token and Accept-Language from URL but preserve intended_url/redirectURL for navigation
      if (urlHadAuthParams) {
        const url = new URL(this._authService.clearTokenParamsFromUrl(window.location.href));
        // intended_url/redirectURL may be path (/analytics/general?tab=summary) or full URL; preserve query string
        const intendedRaw =
          url.searchParams.get('intended_url') || url.searchParams.get('redirectURL') || '';
        const decoded = intendedRaw ? decodeURIComponent(intendedRaw) : '';
        let pathToUse = '';
        if (decoded) {
          if (decoded.startsWith('/')) {
            pathToUse = this._sanitizeUrlForRouting(decoded);
          } else {
            try {
              const asUrl = new URL(decoded, window.location.origin);
              if (asUrl.origin === window.location.origin) {
                pathToUse = this._sanitizeUrlForRouting(asUrl.pathname + asUrl.search);
              }
            } catch {
              // ignore invalid URL
            }
          }
        }
        if (!pathToUse && redirectURL && redirectURL.startsWith('/')) {
          pathToUse = redirectURL;
        }
        if (!pathToUse) {
          pathToUse =
            window.location.pathname || (redirectURL ? redirectURL.split('?')[0] : '') || '/';
        }
        pathToUse = this._sanitizeUrlForRouting(pathToUse) || '/';
        try {
          sessionStorage.removeItem(AuthGuard.STORED_REDIRECT_URL_KEY);
        } catch {
          // ignore
        }

        // Remove "token" or "access_token" queries from pathToUse if exist
        pathToUse = this._authService.clearTokenParamsFromUrl(pathToUse);

        return this._router.parseUrl(pathToUse);
      }

      try {
        const user = await this.userService.getCurrentUser();
        return this._check(redirectURL, user);
      } catch {
        this._router.navigate(['sign-in'], { queryParams: { redirectURL } });
        return false;
      }
    } else {
      this._router.navigate(['sign-in'], { queryParams: { redirectURL } });
    }
  }

  /**
   * Can activate child
   *
   * @param childRoute
   * @param state
   */
  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const redirectUrl = state.url === '/sign-out' ? '/' : state.url;
    return this.userService.getCurrentUser().then((user) => {
      return this._check(redirectUrl, user);
    });
  }

  /**
   * Can load
   *
   * @param route
   * @param segments
   */
  canLoad(route: Route, segments: UrlSegment[]): Observable<boolean> | Promise<boolean> | boolean {
    return this.userService.getCurrentUser().then((user) => this._check('/', user));
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Check the authenticated status
   *
   * @param redirectURL
   * @private
   */
  private _check(redirectURL: string, user: User): boolean {
    if (this.oauth.isSignedIn()) {
      //temp code for operation engineer
      if (user.role_name != 'ORG_OPERATION_ENGINEER' && redirectURL.split('/')[1] == 'operation') {
        this._router.navigate(['/projects/all_projects']);
      } else if (
        user.role_name == 'ORG_OPERATION_ENGINEER' &&
        redirectURL.split('/')[1] !== 'operation'
      ) {
        if (redirectURL != '/') {
          this._router.navigate(['/operation/pending-reports']);
        }
      }

      return true;
    } else {
      this._router.navigate(['sign-in'], { queryParams: { redirectURL } });
      return false;
    }
  }

  private _sanitizeUrlForRouting(rawUrl: string): string {
    if (!rawUrl) {
      return '';
    }

    const sanitized = this._authService.clearTokenParamsFromUrl(rawUrl);

    try {
      const parsed = new URL(sanitized, window.location.origin);
      if (parsed.origin !== window.location.origin) {
        return '';
      }
      return (
        parsed.pathname + (parsed.search ? parsed.search : '') + (parsed.hash ? parsed.hash : '')
      );
    } catch {
      return '';
    }
  }

  // @deprecated - check signed-in
  // private _check(redirectURL: string): Observable<boolean> {
  // 	// Check the authentication status
  // 	return this._authService.check().pipe(
  // 		switchMap((authenticated) => {
  // 			// If the user is not authenticated...
  // 			if (!authenticated) {
  // 				// Redirect to the sign-in page
  // 				this._router.navigate(["sign-in"], { queryParams: { redirectURL } });
  //
  // 				// Prevent the access
  // 				return of(false);
  // 			}
  //
  // 			//temp code for operation engineer
  //
  // 			if (
  // 				this._authService.userData.role_name != "ORG_OPERATION_ENGINEER" &&
  // 				redirectURL.split("/")[1] == "operation"
  // 			) {
  // 				this._router.navigate(["/projects/all_projects"]);
  // 			} else if (
  // 				this._authService.userData.role_name == "ORG_OPERATION_ENGINEER" &&
  // 				redirectURL.split("/")[1] !== "operation"
  // 			) {
  // 				if (redirectURL != "/") {
  // 					this._router.navigate(["/operation/pending-reports"]);
  // 				}
  // 			}
  //
  // 			//temp code for operation engineer
  //
  // 			// Allow the access
  // 			return of(true);
  // 		}),
  // 	);
  // }
}
